import { Menu, PositionType } from 'common/menu';
import { classnames } from 'helpers/utils';
import {
  EditIcon,
  MicIcon,
  PlusIcon,
  ShareIcon,
  VideoIcon,
} from 'assets/icons';
import { useContext, useEffect, useRef, useState } from 'react';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { SelfRecordingContext } from 'context/self-recording-context';
import { AlbumAddContentTypes, AlbumContentType } from 'common/enums';
import { UploadVideos } from 'common/camera-upload/components/upload-videos';
import { AppContext } from 'context/app-context';
import { useClickOutside } from 'hooks/click-outside';
import styles from './add-content.module.scss';

interface AddContentProps {
  displayType: 'menu' | 'empty-state' | 'button';
  setAddNewContent: (contentType: AlbumAddContentTypes) => void;
  albumId: number;
  onClose?: () => void;
  emptyStateClassName?: string;
}
interface ContentOption {
  id: AlbumContentType;
  name: string;
  icon: typeof VideoIcon;
  action?: () => void;
  component?: React.ReactNode;
}

export const AddContent = ({
  displayType,
  setAddNewContent,
  albumId,
  onClose,
  emptyStateClassName,
}: AddContentProps) => {
  const { dispatch, state } = useContext(SelfRecordingContext);
  const { state: appState } = useContext(AppContext);
  const [showMenu, setShowMenu] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (appState.currentVault?.id) {
      dispatch({
        type: selfRecordingActions.setVaultId,
        vaultId: appState.currentVault?.id,
      });
    }
    dispatch({
      type: selfRecordingActions.setAlbumId,
      albumId,
    });
  }, [dispatch, state.vaultId, albumId]);

  useClickOutside(menuContainerRef, () => {
    if (displayType === 'button' && showMenu) {
      setShowMenu(false);
    }
  });

  const contentOptions: ContentOption[] = [
    {
      id: AlbumContentType.write,
      name: 'Write',
      icon: EditIcon,
      action: () => {
        setAddNewContent(AlbumAddContentTypes.write);
        onClose?.();
      },
    },
    {
      id: AlbumContentType.video,
      name: 'Record video',
      icon: VideoIcon,
      action: () => {
        dispatch({
          type: selfRecordingActions.setMode,
          mode: AlbumAddContentTypes.video,
        });
        setAddNewContent(AlbumAddContentTypes.video);
        onClose?.();
      },
    },
    {
      id: AlbumContentType.audio,
      name: 'Record audio',
      icon: MicIcon,
      action: () => {
        dispatch({
          type: selfRecordingActions.setMode,
          mode: AlbumAddContentTypes.audio,
        });
        setAddNewContent(AlbumAddContentTypes.audio);
        onClose?.();
      },
    },
    {
      id: AlbumContentType.document,
      name: 'Upload',
      icon: ShareIcon,
      action: () => {},
      component: (
        <UploadVideos
          vault={appState.currentVault!}
          key={AlbumContentType.document}
          isEmptyState={displayType === 'empty-state'}
        />
      ),
    },
  ];

  if (displayType === 'button') {
    if (showMenu) {
      const buttonContentOptions: ContentOption[] = [
        {
          id: AlbumContentType.write,
          name: 'Write',
          icon: EditIcon,
          action: () => {
            setAddNewContent(AlbumAddContentTypes.write);
            onClose?.();
            setShowMenu(false);
          },
        },
        {
          id: AlbumContentType.video,
          name: 'Record video',
          icon: VideoIcon,
          action: () => {
            dispatch({
              type: selfRecordingActions.setMode,
              mode: AlbumAddContentTypes.video,
            });
            setAddNewContent(AlbumAddContentTypes.video);
            onClose?.();
            setShowMenu(false);
          },
        },
        {
          id: AlbumContentType.audio,
          name: 'Record audio',
          icon: MicIcon,
          action: () => {
            dispatch({
              type: selfRecordingActions.setMode,
              mode: AlbumAddContentTypes.audio,
            });
            setAddNewContent(AlbumAddContentTypes.audio);
            onClose?.();
            setShowMenu(false);
          },
        },
        {
          id: AlbumContentType.document,
          name: 'Upload',
          icon: ShareIcon,
          action: () => {},
          component: (
            <UploadVideos
              vault={appState.currentVault!}
              key={AlbumContentType.document}
              isEmptyState
            />
          ),
        },
      ];

      return (
        <div
          ref={menuContainerRef}
          className={classnames(
            styles.emptyStateContainer,
            emptyStateClassName ?? '',
          )}
        >
          <div className={styles.plusButton}>
            <PlusIcon />
          </div>
          <div className={styles.optionsContainer}>
            {buttonContentOptions.map(
              (option) =>
                option.component || (
                  <button
                    type="button"
                    key={option.id}
                    className={styles.optionButton}
                    onClick={option.action}
                  >
                    <option.icon />
                    <span>{option.name}</span>
                  </button>
                ),
            )}
          </div>
        </div>
      );
    }

    return (
      <button
        type="button"
        ref={buttonRef}
        className={styles.addEntryButton}
        onClick={() => setShowMenu(true)}
      >
        <PlusIcon />
        <span>Add Entry</span>
      </button>
    );
  }

  return displayType === 'menu' ? (
    <Menu
      position={PositionType.BottomRight}
      width={styles.menuWidth}
      className={styles.menu}
    >
      <div className={styles.menuContainer}>
        {contentOptions.map(
          (option) =>
            option.component || (
              <button
                type="button"
                key={option.id}
                className={classnames(
                  styles[`${option.id}Color`],
                  styles.menuItem,
                )}
                onClick={option.action}
              >
                <option.icon
                  className={classnames(styles.icon, styles[option.id])}
                />
                <span>{option.name}</span>
              </button>
            ),
        )}
      </div>
    </Menu>
  ) : (
    <div
      className={classnames(
        styles.emptyStateContainer,
        emptyStateClassName ?? '',
      )}
    >
      <div className={styles.plusButton}>
        <PlusIcon />
      </div>
      <div className={styles.optionsContainer}>
        {contentOptions.map(
          (option) =>
            option.component || (
              <button
                type="button"
                key={option.id}
                className={styles.optionButton}
                onClick={option.action}
              >
                <option.icon />
                <span>{option.name}</span>
              </button>
            ),
        )}
      </div>
    </div>
  );
};
